import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import PrintIcon from '@mui/icons-material/Print';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import {LoadingButton} from '@mui/lab';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  MenuItem,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useConfiguration} from '../../../hooks/configuration';
import {useAppSelector} from '../../../hooks/redux';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  Event,
  EventListResponse,
  HazardEventListQuery,
} from '../../../interfaces/Event';
import {ExportField} from '../../../interfaces/Export';
import {eventBaseTypesSelect} from '../../../redux/assets/selectors';
import {getEventDescription} from '../../../utils/events';
import AccessControl from '../../common/AccessControl';
import {AutoRefreshSettingsSelect} from '../../common/AutoRefreshSettingsSelect';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import SnackbarMessages from '../../common/SnackbarMessages';
import EventExportButton from '../../events/EventExportButton';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {EventTypeSelect} from '../../selectors/EventTypeSelect';

interface Props {
  value?: EventsReportData;
  fetchedData?: EventListResponse;
  loading?: boolean;
  onChange?: (value?: EventsReportData) => void;
  onRefresh?: () => void;
}

export interface EventsReportData {
  params: Partial<HazardEventListQuery>;
  selectedIds: number[] | null;
  shownFields: string[];
  exportFields: ExportField[];
  columnWidths: Record<string, number>;
  columnOrder?: string[];
}

const DEFAULT_SHOWN_FIELDS = [
  'date',
  'ack',
  'text_replacements',
  'name',
  'network_id',
  'base_type',
  'cleared',
];

export const getEventsReportData = (): EventsReportData => {
  return {
    params: {
      status: 'all',
      date_start: dayjs().format('YYYY-MM-DD'),
      date_end: dayjs().format('YYYY-MM-DD'),
      limit: 10,
      page: 0,
      type: [],
    },
    selectedIds: [],
    shownFields: DEFAULT_SHOWN_FIELDS,
    exportFields: [],
    columnWidths: {},
    columnOrder: undefined,
  };
};

const EventsReport = ({
  value,
  fetchedData,
  loading,
  onChange,
  onRefresh,
}: Props) => {
  const config = useMemo(() => value ?? getEventsReportData(), [value]);

  /*************/
  /* data grid */
  /*************/
  const eventBaseTypes = useAppSelector(eventBaseTypesSelect);
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<Event>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(event) =>
            toggleSelectItem(
              row.id,
              isShiftClick(event),
              visibleRows.map((visibleRow) => visibleRow.id)
            )
          }
        />
      ),
      doNotExport: true,
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'date',
      headerName: 'Timestamp',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'ack',
      headerName: 'Ack',
      width: 40,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}: any) => {
        return row.ack === '1';
      },
      renderCell: ({value}) => {
        if (value) {
          return <CheckCircleIcon color="success" />;
        } else {
          return <RadioButtonUncheckedIcon color="success" />;
        }
      },
    },
    {
      field: 'text_replacements',
      headerName: 'Event Description',
      width: 500,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}: any) => {
        return getEventDescription(row.text_template, row.text_replacements);
      },
    },
    {
      field: 'name',
      headerName: 'Nickname',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}: any) => {
        return row.name;
      },
    },
    {
      field: 'network_id',
      headerName: 'Network ID',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => {
        if (row.network_id) {
          return row.network_id;
        }
      },
    },
    {
      field: 'base_type',
      headerName: 'Type',
      width: 150,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}: any) => {
        const idx = eventBaseTypes.findIndex((t) => t.type === row.base_type);
        if (idx === -1) {
          return 'Unknown Event Type';
        }
        return eventBaseTypes[idx].name;
      },
    },
    {
      field: 'cleared',
      headerName: 'Cleared',
      width: 60,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}: any) => {
        if (row.cleared === '1') {
          return 'Yes';
        } else {
          return 'No';
        }
      },
    },
  ];
  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = config?.selectedIds ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    const nextSelectedIds = getNextSelectedIds({
      id,
      visibleIds,
      selectedIds: selectedItems ?? [],
      shiftKey,
    });
    onChange?.(
      update(config, {
        selectedIds: {
          $set: nextSelectedIds,
        },
      })
    );
  };

  const selectAll = () => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: rows?.map((i) => i.id) ?? [],
        },
      })
    );
  };

  const unselectAll = () => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: [],
        },
      })
    );
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  /********************/
  /* refresh interval */
  /********************/
  const refreshPeriod = useConfiguration(
    'hazard-ai',
    'hazard_ai_event_autorefresh_rate'
  );

  /**************/
  /* submit ack */
  /**************/
  const {enqueueSnackbar} = useSnackbar();
  const [submittedAckInProgress, setSubmittedAckInProgress] = useState(false);

  const submitAckEvent = async () => {
    setSubmittedAckInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/event/acknowledge`;
      await API.patch(endpoint, {
        ids: {
          hazard_ai: config.selectedIds,
        },
      });
      const message = `Event(s) was successfully changed`;
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onRefresh?.();
      unselectAll();
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_95580cc3");
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedAckInProgress(false);
  };

  useEffect(() => {
    onChange?.(
      update(config, {
        exportFields: {
          $set: columns
            .filter((col) => !col.doNotExport)
            .map((col) => ({
              field: col.field,
              label: col.headerName,
              hidden: config.shownFields?.indexOf(col.field) === -1,
            })),
        },
      })
    );
  }, [config.shownFields]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onChange?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onChange?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
        })
      );
    },
    [onChange, config]
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        p={2}
      >
        <Box display="flex" gap={1}>
          <Box minWidth={400}>
            <DateRangeSelect
              value={[
                dayjs(config.params.date_start).toDate(),
                dayjs(config.params.date_end).toDate(),
              ]}
              size="small"
              onChange={(v) => {
                onChange?.(
                  update(config, {
                    params: {
                      date_start: {
                        $set: v?.[0]
                          ? dayjs(v?.[0]).format('YYYY-MM-DD')
                          : undefined,
                      },
                      date_end: {
                        $set: v?.[0]
                          ? dayjs(v?.[1]).format('YYYY-MM-DD')
                          : undefined,
                      },
                      page: {
                        $set: 0,
                      },
                    },
                  })
                );
              }}
            />
          </Box>

          <TextField
            value={config.params.acknowledged ?? 'all'}
            label="Acknowledged"
            name="acknowledged"
            select
            size="small"
            sx={{width: 170}}
            onChange={(e) => {
              const v =
                e.target.value === 'all'
                  ? undefined
                  : e.target.value === 'true';
              onChange?.(
                update(config, {
                  params: {
                    acknowledged: {
                      $set: v,
                    },
                    page: {
                      $set: 0,
                    },
                  },
                })
              );
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Acknowledged</MenuItem>
            <MenuItem value="false">Not Acknowledged</MenuItem>
          </TextField>

          <TextField
            value={config.params.cleared ?? 'all'}
            name="cleared"
            label="Cleared"
            select
            sx={{width: 130}}
            size="small"
            onChange={(e) => {
              const v =
                e.target.value === 'all'
                  ? undefined
                  : e.target.value === 'true';
              onChange?.(
                update(config, {
                  params: {
                    cleared: {
                      $set: v,
                    },
                    page: {
                      $set: 0,
                    },
                  },
                })
              );
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Cleared</MenuItem>
            <MenuItem value="false">Not Cleared</MenuItem>
          </TextField>

          <Box width={300}>
            <EventTypeSelect
              value={config.params.type}
              onChange={(v) => {
                onChange?.(
                  update(config, {
                    params: {
                      type: {
                        $set: v,
                      },
                      page: {
                        $set: 0,
                      },
                    },
                  })
                );
              }}
            />
          </Box>
        </Box>

        <Box display="flex">
          <ButtonGroup>
            <AccessControl permissions={['patch::/event/acknowledge']}>
              <LoadingButton
                size="small"
                disabled={!config.selectedIds?.length}
                loading={submittedAckInProgress}
                variant="outlined"
                onClick={submitAckEvent}
              >
                ACK
              </LoadingButton>
            </AccessControl>

            <EventExportButton
              value={value}
              component={Button}
              componentProps={{
                color: 'primary',
              }} /*onSubmitted={() => onRefresh?.()}*/
            >
              <ImportExportIcon />
            </EventExportButton>

            <Button size="small" onClick={() => onRefresh?.()}>
              <RefreshIcon />
            </Button>

            {refreshPeriod ? (
              <AutoRefreshSettingsSelect refreshPeriod={refreshPeriod} />
            ) : null}

            <Button
              size="small"
              onClick={() => dataGridRef.current?.printTable()}
            >
              <PrintIcon />
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={loading}
        shownFields={config.shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config.columnWidths}
        columnOrder={config.columnOrder}
        pagination
        paginationMode="server"
        size="small"
        sortBy={
          config.params.order
            ? {
                field: config.params.order,
                dir: config.params.dir === 'DESC' ? 'desc' : 'asc',
              }
            : null
        }
        sortingMode="server"
        page={config.params.page}
        pageSize={config.params.limit}
        rowCount={fetchedData?.count}
        sxFooter={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
        }}
        onPageChange={(v) => {
          onChange?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: v,
                },
              },
            })
          );
        }}
        onPageSizeChange={(v) => {
          onChange?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: 0,
                  limit: v,
                },
              },
            })
          );
        }}
        onSort={(v) => {
          if (v) {
            onChange?.(
              update(config, {
                params: {
                  $set: {
                    ...config.params,
                    order: v.field,
                    dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                  },
                },
              })
            );
          }
        }}
        onShownFieldsChange={(v) => {
          onChange?.(
            update(config, {
              shownFields: {
                $set: v,
              },
            })
          );
        }}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
      />
    </Box>
  );
};

export default EventsReport;
